/**
 * Client Reports — thin client over the REAL per-region report API
 * (CPT-1177/1178/1179), mounted at /api/analytics/client-reports so it inherits
 * the insights_manager / insights_client gate the /api/analytics family already
 * carries. See server/src/routes/clientReports.ts for the authoritative shapes.
 *
 * The IndexedDB mock this replaced is gone, along with its "hold every region's
 * HTML in the browser" model: report bytes are fetched ONE region at a time via
 * getContent(), which is the enforcement point (403 region_not_permitted).
 */
import axiosClient from './axiosClient';
import type {
  ClientReport,
  ClientReportMetadataPatch,
  ClientReportUploadPayload,
} from '../pages/Insights/types';

const BASE = '/api/analytics/client-reports';

/** One entry of GET /:id/regions — a sibling slot that is live AND permitted. */
export interface ReportRegionRef {
  id: string;
  regionSlot: string;
}

export interface ReportRegions {
  uploadGroupId: string;
  regions: ReportRegionRef[];
}

export interface ClientReportUploadResult {
  uploadGroupId: string;
  reports: ClientReport[];
}

/** 422 body of POST / when the client-side region split doesn't isolate data. */
export interface RegionSplitInvalidError {
  error: 'region_split_invalid';
  errors: Array<{ region: string; message: string }>;
}

export const clientReportsApi = {
  /** Every ACTIVE row for the tenant — all slots, live and available. */
  list: async (tenantCode: string): Promise<ClientReport[]> => {
    const { data } = await axiosClient.get(BASE, { params: { tenant: tenantCode } });
    return data;
  },

  /** One upload group: one row per region slot, all validated server-side first. */
  upload: async (payload: ClientReportUploadPayload): Promise<ClientReportUploadResult> => {
    const { data } = await axiosClient.post(BASE, payload);
    return data;
  },

  updateMetadata: async (id: string, patch: ClientReportMetadataPatch): Promise<ClientReport> => {
    const { data } = await axiosClient.patch(`${BASE}/${id}`, patch);
    return data;
  },

  /** Make live for this row's slot, demoting whoever holds it. 409 on a race. */
  setLive: async (id: string): Promise<ClientReport> => {
    const { data } = await axiosClient.post(`${BASE}/${id}/publish`);
    return data;
  },

  unpublish: async (id: string): Promise<ClientReport> => {
    const { data } = await axiosClient.post(`${BASE}/${id}/unpublish`);
    return data;
  },

  delete: async (id: string): Promise<{ deleted: boolean; id: string }> => {
    const { data } = await axiosClient.delete(`${BASE}/${id}`);
    return data;
  },

  /** Sibling slots of this row's upload group that are live AND permitted for
   *  the caller. Carries NO content — this is what builds the tab bar. */
  getRegions: async (id: string): Promise<ReportRegions> => {
    const { data } = await axiosClient.get(`${BASE}/${id}/regions`);
    return data;
  },

  /**
   * ONE region slot's raw HTML (text/html, not JSON). The enforcement-critical
   * read: the server re-checks the caller's zones before touching S3, so this
   * can 403 `region_not_permitted` even right after getRegions() listed the slot
   * (permissions can change between the two calls).
   */
  getContent: async (id: string): Promise<string> => {
    const { data } = await axiosClient.get<string>(`${BASE}/${id}/content`, {
      responseType: 'text',
      // The body is HTML — keep axios from trying to JSON.parse it.
      transformResponse: [(raw: unknown) => raw],
    });
    return data;
  },
};
